/**
 * Shared job-application statuses between @upercut/b2b and @upercut/cms.
 *
 * Only the Premium pipeline constants are shared here, because they are
 * byte-for-byte identical across both packages. Classic-pipeline statuses
 * are intentionally NOT shared: their sub-status *values* differ between
 * b2b ("To Schedule") and cms ("ToSchedule"), and the status labels differ
 * ("Nouveau" vs "Candidature"), so unifying them would change persisted data
 * or UI text. Keep classic statuses in each package's own module.
 *
 * ── Premium Pipeline Scope ──
 * The premium pipeline ends at DC.Sent. After that, the hiring process is
 * managed by clients via the classic `status` / `subStatus` fields on the
 * job-application model.
 *
 * Remaining premium statuses: New, UpercutInterview, DC
 * Remaining premium sub-statuses:
 *   New.ToContact, New.ToFollowUp, New.NewRejected, New.SystemRejected,
 *   UpercutInterview.ToSchedule, UpercutInterview.Scheduled,
 *   UpercutInterview.Conducted, UpercutInterview.Canceled,
 *   UpercutInterview.ToReschedule, UpercutInterview.Rejected,
 *   DC.ToCreate, DC.InProgress, DC.Completed, DC.Sent
 *
 * Interview, Offer, Hired and Rejected (and their sub-statuses) are
 * disabled — those stages belong to the client-managed pipeline.
 */

export enum PremiumJobApplicationStatus {
    New = 'New',
    // ── Nice to have (out of Phase 2 automation scope) ──
    // No Phase 2 automation handlers are planned for these statuses.
    // Uncomment when Phase 3 automation scope is defined.
    UpercutInterview = 'UpercutInterview',
    // Questionnaire = 'Questionnaire',
    DC = 'DC',
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // Interview = 'Interview',
    // Trial = 'Trial',
    // Offer = 'Offer',
    // DocumentRequest = 'DocumentRequest',
    // DPAE = 'DPAE',
    // ProbationPeriod = 'ProbationPeriod',
    // Hired = 'Hired',
    // Rejection can occur at any stage of the premium pipeline
    Rejected = 'Rejected',
}

export enum PremiumJobApplicationSubStatus {
    // ── Nice to have (out of Phase 2 automation scope) ──
    // These sub-statuses have no Phase 2 automation handlers. They may be
    // restored when Phase 3 scope is defined.
    Talent = 'Talent',
    Waiting = 'Waiting',
    Canceled = 'Canceled',
    ToReschedule = 'ToReschedule',
    Completed = 'Completed',
    Validated = 'Validated',
    ToSend = 'ToSend',
    ToDo = 'ToDo',
    InProgress = 'InProgress',
    Received = 'Received',
    Upcoming = 'Upcoming',
    AtRisk = 'AtRisk',
    EmployeeResigned = 'EmployeeResigned',
    EmployerTerminated = 'EmployerTerminated',
    ToCreate = 'ToCreate',

    // ── Phase 2 in-scope sub-statuses ──
    ToContact = 'ToContact',
    ToFollowUp = 'ToFollowUp',
    NewRejected = 'NewRejected',
    ToSchedule = 'ToSchedule',
    Scheduled = 'Scheduled',
    Conducted = 'Conducted',
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // InterviewRejected = 'InterviewRejected',
    /** Kept for data backward-compatibility — existing records may reference it,
     *  and *Rejected → TwilioSmsHandler handles it. The Trial parent status is
     *  disabled (nice to have) so this sub-status is not reachable via the UI. */
    TrialRejected = 'TrialRejected',
    Sent = 'Sent',
    // Accepted = 'Accepted',
    // OfferRejected = 'OfferRejected',
    // Premium-exclusive
    SystemRejected = 'SystemRejected',
    Rejected = 'Rejected',
}

export const premiumJobApplicationStatusLabels: Record<PremiumJobApplicationStatus, string> = {
    [PremiumJobApplicationStatus.New]: 'Nouveau',
    // ── Nice to have (out of Phase 2 automation scope) ──
    [PremiumJobApplicationStatus.UpercutInterview]: 'Entretien Upercut',
    // [PremiumJobApplicationStatus.Questionnaire]: 'Questionnaire',
    [PremiumJobApplicationStatus.DC]: 'Dossier de Candidature',
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // [PremiumJobApplicationStatus.Interview]: 'Entretien Client',
    // [PremiumJobApplicationStatus.Trial]: 'Essai / Business Case',
    // [PremiumJobApplicationStatus.Offer]: 'Proposition',
    // [PremiumJobApplicationStatus.DocumentRequest]: 'Demande documents candidat',
    // [PremiumJobApplicationStatus.DPAE]: 'DPAE',
    // [PremiumJobApplicationStatus.ProbationPeriod]: "Période d'essai",
    // [PremiumJobApplicationStatus.Hired]: 'Embauché',
    [PremiumJobApplicationStatus.Rejected]: 'Refusé',
};

export const premiumJobApplicationSubStatusLabels: Record<
    PremiumJobApplicationStatus,
    Partial<Record<PremiumJobApplicationSubStatus, string>> | null
> = {
    [PremiumJobApplicationStatus.New]: {
        // ── Nice to have (out of Phase 2 automation scope) ──
        // [PremiumJobApplicationSubStatus.Talent]: 'À traiter',
        [PremiumJobApplicationSubStatus.ToContact]: 'À contacter',
        [PremiumJobApplicationSubStatus.ToFollowUp]: 'À relancer',
        [PremiumJobApplicationSubStatus.NewRejected]: 'Refusé',
        [PremiumJobApplicationSubStatus.SystemRejected]: 'Pré-refusé (Système)',
        [PremiumJobApplicationSubStatus.Waiting]: 'En attente du process en cours',
    },
    // ── Nice to have — entire status out of Phase 2 automation scope ──
    [PremiumJobApplicationStatus.UpercutInterview]: {
        [PremiumJobApplicationSubStatus.ToSchedule]: 'À planifier',
        [PremiumJobApplicationSubStatus.Scheduled]: 'Planifié',
        [PremiumJobApplicationSubStatus.Conducted]: 'Réalisé',
        [PremiumJobApplicationSubStatus.Canceled]: 'Annulé',
        [PremiumJobApplicationSubStatus.ToReschedule]: 'À reprogrammer',
        [PremiumJobApplicationSubStatus.Rejected]: 'Refusé',
    },
    // [PremiumJobApplicationStatus.Questionnaire]: {
    //     [PremiumJobApplicationSubStatus.ToSend]: 'À envoyer',
    //     [PremiumJobApplicationSubStatus.Sent]: 'Envoyé',
    //     [PremiumJobApplicationSubStatus.Completed]: 'Réalisé',
    //     [PremiumJobApplicationSubStatus.Rejected]: 'Refusé',
    // },
    [PremiumJobApplicationStatus.DC]: {
        [PremiumJobApplicationSubStatus.ToCreate]: 'À créer',
        [PremiumJobApplicationSubStatus.InProgress]: 'En cours',
        [PremiumJobApplicationSubStatus.Completed]: 'Terminé',
        [PremiumJobApplicationSubStatus.Sent]: 'Transmis envoyé',
    },
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // [PremiumJobApplicationStatus.Interview]: {
    //     [PremiumJobApplicationSubStatus.ToSchedule]: 'À planifier',
    //     [PremiumJobApplicationSubStatus.Scheduled]: 'Planifié',
    //     [PremiumJobApplicationSubStatus.Conducted]: 'Réalisé',
    //     // ── Nice to have (out of Phase 2 automation scope) ──
    //     // [PremiumJobApplicationSubStatus.Canceled]: 'Annulé',
    //     // [PremiumJobApplicationSubStatus.ToReschedule]: 'À reprogrammer',
    //     [PremiumJobApplicationSubStatus.InterviewRejected]: 'Refusé',
    // },
    // [PremiumJobApplicationStatus.Trial]: {
    //     [PremiumJobApplicationSubStatus.ToSchedule]: 'À planifier',
    //     [PremiumJobApplicationSubStatus.Scheduled]: 'Planifié',
    //     [PremiumJobApplicationSubStatus.Completed]: 'Réalisé',
    //     [PremiumJobApplicationSubStatus.Canceled]: 'Annulé',
    //     [PremiumJobApplicationSubStatus.TrialRejected]: 'Refusé',
    //     [PremiumJobApplicationSubStatus.Validated]: 'Validé',
    // },
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // [PremiumJobApplicationStatus.Offer]: {
    //     // ── Nice to have (out of Phase 2 automation scope) ──
    //     // [PremiumJobApplicationSubStatus.ToSend]: 'À envoyer',
    //     [PremiumJobApplicationSubStatus.Sent]: 'Envoyée',
    //     [PremiumJobApplicationSubStatus.Accepted]: 'Acceptée',
    //     [PremiumJobApplicationSubStatus.OfferRejected]: 'Refusée',
    // },
    // ── Nice to have — entire status out of Phase 2 automation scope ──
    // [PremiumJobApplicationStatus.DocumentRequest]: {
    //     [PremiumJobApplicationSubStatus.ToDo]: 'À faire',
    //     [PremiumJobApplicationSubStatus.InProgress]: 'En cours',
    //     [PremiumJobApplicationSubStatus.Received]: 'Docs reçus',
    // },
    // [PremiumJobApplicationStatus.DPAE]: {
    //     [PremiumJobApplicationSubStatus.ToDo]: 'À faire',
    //     [PremiumJobApplicationSubStatus.InProgress]: 'En cours',
    //     [PremiumJobApplicationSubStatus.Completed]: 'Réalisée',
    // },
    // [PremiumJobApplicationStatus.ProbationPeriod]: {
    //     [PremiumJobApplicationSubStatus.Upcoming]: 'À venir',
    //     [PremiumJobApplicationSubStatus.InProgress]: 'En cours',
    //     [PremiumJobApplicationSubStatus.AtRisk]: 'À risque',
    //     [PremiumJobApplicationSubStatus.EmployeeResigned]: 'Rupture salarié',
    //     [PremiumJobApplicationSubStatus.EmployerTerminated]: 'Rupture employeur',
    // },
    // [PremiumJobApplicationStatus.Hired]: null,
    [PremiumJobApplicationStatus.Rejected]: null,
};

/**
 * Allowed sub-statuses per premium status, in canonical display order.
 *
 * The premium pipeline ends at DC.Sent — after that the client manages the
 * process via the classic `status` / `subStatus` fields.
 *
 * Active statuses:
 *   New:           ToContact, ToFollowUp, NewRejected, SystemRejected
 *   UpercutInterview: ToSchedule, Scheduled, Conducted, Canceled, ToReschedule, Rejected
 *   DC:            ToCreate, InProgress, Completed, Sent
 */
export const premiumJobApplicationStatuses: Record<
    PremiumJobApplicationStatus,
    PremiumJobApplicationSubStatus[]
> = {
    [PremiumJobApplicationStatus.New]: [
        // ── Nice to have (out of Phase 2 automation scope) ──
        // PremiumJobApplicationSubStatus.Talent,
        PremiumJobApplicationSubStatus.ToContact,
        PremiumJobApplicationSubStatus.ToFollowUp,
        PremiumJobApplicationSubStatus.NewRejected,
        PremiumJobApplicationSubStatus.SystemRejected,
        PremiumJobApplicationSubStatus.Waiting,
    ],
    // ── Nice to have — entire status out of Phase 2 automation scope ──
    [PremiumJobApplicationStatus.UpercutInterview]: [
        PremiumJobApplicationSubStatus.ToSchedule,
        PremiumJobApplicationSubStatus.Scheduled,
        PremiumJobApplicationSubStatus.Conducted,
        PremiumJobApplicationSubStatus.Canceled,
        PremiumJobApplicationSubStatus.ToReschedule,
        PremiumJobApplicationSubStatus.Rejected,
    ],
    // [PremiumJobApplicationStatus.Questionnaire]: [
    //     PremiumJobApplicationSubStatus.ToSend,
    //     PremiumJobApplicationSubStatus.Sent,
    //     PremiumJobApplicationSubStatus.Completed,
    //     PremiumJobApplicationSubStatus.Rejected,
    // ],
    [PremiumJobApplicationStatus.DC]: [
        PremiumJobApplicationSubStatus.ToCreate,
        PremiumJobApplicationSubStatus.InProgress,
        PremiumJobApplicationSubStatus.Completed,
        PremiumJobApplicationSubStatus.Sent,
    ],
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // [PremiumJobApplicationStatus.Interview]: [
    //     PremiumJobApplicationSubStatus.ToSchedule,
    //     PremiumJobApplicationSubStatus.Scheduled,
    //     PremiumJobApplicationSubStatus.Conducted,
    //     // ── Nice to have (out of Phase 2 automation scope) ──
    //     // PremiumJobApplicationSubStatus.Canceled,
    //     // PremiumJobApplicationSubStatus.ToReschedule,
    //     PremiumJobApplicationSubStatus.InterviewRejected,
    // ],
    // [PremiumJobApplicationStatus.Trial]: [
    //     PremiumJobApplicationSubStatus.ToSchedule,
    //     PremiumJobApplicationSubStatus.Scheduled,
    //     PremiumJobApplicationSubStatus.Completed,
    //     PremiumJobApplicationSubStatus.Canceled,
    //     PremiumJobApplicationSubStatus.TrialRejected,
    //     PremiumJobApplicationSubStatus.Validated,
    // ],
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // [PremiumJobApplicationStatus.Offer]: [
    //     // ── Nice to have (out of Phase 2 automation scope) ──
    //     // PremiumJobApplicationSubStatus.ToSend,
    //     PremiumJobApplicationSubStatus.Sent,
    //     PremiumJobApplicationSubStatus.Accepted,
    //     PremiumJobApplicationSubStatus.OfferRejected,
    // ],
    // ── Nice to have — entire status out of Phase 2 automation scope ──
    // [PremiumJobApplicationStatus.DocumentRequest]: [
    //     PremiumJobApplicationSubStatus.ToDo,
    //     PremiumJobApplicationSubStatus.InProgress,
    //     PremiumJobApplicationSubStatus.Received,
    // ],
    // [PremiumJobApplicationStatus.DPAE]: [
    //     PremiumJobApplicationSubStatus.ToDo,
    //     PremiumJobApplicationSubStatus.InProgress,
    //     PremiumJobApplicationSubStatus.Completed,
    // ],
    // [PremiumJobApplicationStatus.ProbationPeriod]: [
    //     PremiumJobApplicationSubStatus.Upcoming,
    //     PremiumJobApplicationSubStatus.InProgress,
    //     PremiumJobApplicationSubStatus.AtRisk,
    //     PremiumJobApplicationSubStatus.EmployeeResigned,
    //     PremiumJobApplicationSubStatus.EmployerTerminated,
    // ],
    // [PremiumJobApplicationStatus.Hired]: [],
    [PremiumJobApplicationStatus.Rejected]: [],
};

/**
 * High-level ordered stages for the premium pipeline stepper.
 * The premium pipeline ends at DC.Sent — after that the client manages the
 * process via the classic `status` / `subStatus` fields.
 */
export const PREMIUM_PIPELINE_STAGES: PremiumJobApplicationStatus[] = [
    PremiumJobApplicationStatus.New,
    // ── Nice to have (out of Phase 2 automation scope) ──
    PremiumJobApplicationStatus.UpercutInterview,
    // PremiumJobApplicationStatus.Questionnaire,
    PremiumJobApplicationStatus.DC,
    // ── Client-managed (after DC.Sent, clients handle via status/subStatus) ──
    // PremiumJobApplicationStatus.Interview,
    // PremiumJobApplicationStatus.Trial,
    // PremiumJobApplicationStatus.Offer,
    // PremiumJobApplicationStatus.DocumentRequest,
    // PremiumJobApplicationStatus.DPAE,
    // PremiumJobApplicationStatus.ProbationPeriod,
    // PremiumJobApplicationStatus.Hired,
];
